import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import VerificationCodeField from '../Components/VerificationCodeField';
import GuestTrackResultCard from '../Components/GuestTrackResultCard';
import axiosInstance from '../axiosConfig';

const GuestTrackRequest = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';
  const verificationCodeRef = useRef(null);

  const [requestNumber, setRequestNumber] = useState('');
  const [requestDate, setRequestDate] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [challengeCode, setChallengeCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [trackResult, setTrackResult] = useState(null);
  const [trackError, setTrackError] = useState('');

  useEffect(() => {
    if (location.state?.requestNumber) {
      setRequestNumber(location.state.requestNumber);
    }

    if (location.state) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const handleTrack = async (event) => {
    event.preventDefault();
    if (!requestNumber.trim() || !requestDate || !challengeId || challengeCode.trim().length !== 4) {
      return;
    }

    setSearching(true);
    setTrackResult(null);
    setTrackError('');

    try {
      const result = await axiosInstance.post('/Guest/TrackRequest', {
        requestNumber: requestNumber.trim(),
        insertDate: requestDate,
        challengeId,
        challengeCode: challengeCode.trim(),
      });
      setTrackResult(result);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || '';
      setTrackError(
        message.toLowerCase().includes('verification code') || message.toLowerCase().includes('challenge')
          ? t('verificationCode_invalid')
          : message.toLowerCase().includes('no request')
          ? t('guest_track_notFound')
          : t('guest_track_error')
      );
    } finally {
      setSearching(false);
      await verificationCodeRef.current?.refresh();
    }
  };

  const formatDate = (value) => {
    if (!value) {
      return '—';
    }

    try {
      return new Date(value).toLocaleDateString(isRTL ? 'ar-SA' : 'en-GB');
    } catch {
      return value;
    }
  };

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 4,
            background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, #ffffff 55%, ${alpha('#0f9d58', 0.08)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box sx={{ position: 'absolute', top: -50, right: isRTL ? 'auto' : -50, left: isRTL ? -50 : 'auto', width: 180, height: 180, borderRadius: '50%', background: alpha(theme.palette.primary.main, 0.08) }} />
          <Box sx={{ position: 'absolute', bottom: -60, left: isRTL ? 'auto' : -30, right: isRTL ? -30 : 'auto', width: 200, height: 200, borderRadius: '50%', background: alpha('#0f9d58', 0.06) }} />

          <Stack spacing={3} sx={{ position: 'relative' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{
                  width: 54,
                  height: 54,
                  borderRadius: 3,
                  background: alpha(theme.palette.primary.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <TrackChangesIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    {t('guest_track_title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('guest_track_screenSubtitle')}
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="text"
                onClick={() => navigate('/guest-services')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {t('nav_guestServices')}
              </Button>
            </Stack>

            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, backgroundColor: alpha('#fff', 0.82), border: `1px solid ${alpha(theme.palette.primary.main, 0.09)}` }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.8 }}>
                {t('guest_track_subtitle')}
              </Typography>

              <Box component="form" onSubmit={handleTrack}>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <TextField
                      fullWidth
                      label={t('guest_track_numberLabel')}
                      placeholder={t('guest_track_numberPlaceholder')}
                      value={requestNumber}
                      onChange={(event) => setRequestNumber(event.target.value)}
                      required
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                    <TextField
                      fullWidth
                      type="date"
                      label={t('guest_track_dateLabel')}
                      value={requestDate}
                      onChange={(event) => setRequestDate(event.target.value)}
                      required
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: new Date().toISOString().split('T')[0] }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                  </Stack>

                  <VerificationCodeField
                    ref={verificationCodeRef}
                    value={challengeCode}
                    onValueChange={setChallengeCode}
                    onChallengeIdChange={setChallengeId}
                    disabled={searching}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={searching || !requestNumber.trim() || !requestDate || !challengeId || challengeCode.trim().length !== 4}
                    startIcon={searching ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                    sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 5, fontSize: '1rem', alignSelf: 'flex-start' }}
                  >
                    {t('guest_track_btn')}
                  </Button>
                </Stack>
              </Box>
            </Paper>

            {trackError && (
              <Alert severity="warning" sx={{ borderRadius: 3 }} onClose={() => setTrackError('')}>
                {trackError}
              </Alert>
            )}

            {trackResult && <GuestTrackResultCard result={trackResult} formatDate={formatDate} />}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default GuestTrackRequest;
